import i18n from '@/i18n';
import { StudyYearPeriodType, type StudyYearPeriod } from '@/types/overview';
import { formatDate, startOfDay } from '@/utils/date';
import { RANGE_SYMBOL } from '@/utils/text';

const FIRST_SEMESTER_START_MONTH = 8;

export const getCurrentStudyYearStart = (): number => {
  const today = new Date();

  return today.getMonth() >= FIRST_SEMESTER_START_MONTH
    ? today.getFullYear()
    : today.getFullYear() - 1;
};

export const parseStudyYearStart = (
  studyYearName?: string,
): number | undefined => {
  const startYear = studyYearName?.trim().match(/^\d{4}/)?.[0];

  return startYear ? Number(startYear) : undefined;
};

export const getFallbackPeriods = (
  studyYearStart: number,
): StudyYearPeriod[] => [
  {
    name: i18n.t('overview.attendance.period.firstSemester'),
    from: new Date(studyYearStart, 8, 1),
    to: new Date(studyYearStart, 11, 31),
    type: StudyYearPeriodType.Semester,
  },
  {
    name: i18n.t('overview.attendance.period.secondSemester'),
    from: new Date(studyYearStart + 1, 0, 1),
    to: new Date(studyYearStart + 1, 7, 31),
    type: StudyYearPeriodType.Semester,
  },
  {
    name: i18n.t('overview.attendance.period.year'),
    from: new Date(studyYearStart, 8, 1),
    to: new Date(studyYearStart + 1, 7, 31),
    type: StudyYearPeriodType.Year,
  },
];

export const getDefaultPeriod = (periods: StudyYearPeriod[]): StudyYearPeriod =>
  periods.find((period) => period.type === StudyYearPeriodType.Year) ??
  periods[0];

export const hasSameDates = (
  period: StudyYearPeriod,
  otherPeriod: StudyYearPeriod,
): boolean =>
  period.from.getTime() === otherPeriod.from.getTime() &&
  period.to.getTime() === otherPeriod.to.getTime();

export const containsPeriod = (
  outerPeriod: StudyYearPeriod,
  period: StudyYearPeriod,
): boolean =>
  startOfDay(period.from).getTime() >= startOfDay(outerPeriod.from).getTime() &&
  startOfDay(period.to).getTime() <= startOfDay(outerPeriod.to).getTime();

export const isSamePeriod = (
  period: StudyYearPeriod,
  otherPeriod: StudyYearPeriod,
): boolean =>
  period.name === otherPeriod.name && hasSameDates(period, otherPeriod);

export const formatPeriodRange = ({ from, to }: StudyYearPeriod): string =>
  `${formatDate(from.toISOString())} ${RANGE_SYMBOL} ${formatDate(to.toISOString())}`;

export const createCustomPeriod = (from: Date, to: Date): StudyYearPeriod => ({
  name: i18n.t('overview.attendance.period.custom'),
  from,
  to,
  type: StudyYearPeriodType.Custom,
});
